import {
  KDS_DEFAULTS,
  KDS_TAB_LABEL,
  KDS_TAB_PATH,
  ageBucket,
  ageSeconds,
  canUndo,
  effectiveThresholds,
  formatAge,
  kdsTabOf,
  otherTabs,
  resolveDeviceStation,
  sortTickets,
} from '@didto/domain';
import type { AgeThresholds, KdsStation, KdsTab } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Select,
  Sheet,
  Surface,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { useClock } from '@/app/useClock';
import { useSetting } from '@/app/useSetting';
import { errorText } from '@/lib/errors';
import {
  fetchStation,
  fetchStationCancelled,
  fetchStationQueue,
  fetchStationRecent,
  fetchStations,
  fetchStationsWaiting,
  kdsKeys,
  ticketItems,
} from '@/lib/kds';
import type { ProductionItem, Ticket } from '@/lib/kds';
import { useOnline } from '@/lib/network';
import { ORDER_TYPE_LABEL } from '@/lib/orders';
import { topicStation, useChannelState, useRealtimeInvalidate } from '@/lib/realtime';
import { useTables } from '../pos/posData';
import { ReasonSheet } from '../pos/sheets';
import type { ReasonRequest } from '../pos/sheets';
import {
  playCancelled,
  playNewTicket,
  primeAudio,
  kdsHeard,
  readSoundChoice,
  useKdsActions,
  useWakeLock,
  writeSoundChoice,
} from './kdsSupport';
import type { ActionOutcome, PendingAction } from './kdsSupport';
import {
  AgeClock,
  ItemState,
  LivePill,
  OrderNo,
  TicketState,
  Unconfirmed,
  clockTime,
  lineMeta,
} from './live';
import './kds.css';

/**
 * Kitchen / Bar display (Phase 5, ADR-020; ADR-045). The page's route chooses the tab: /kitchen shows a
 * Kitchen-route station, /bar the Bar station. A device shows a tab's station only when the station is
 * assigned to it (a display may have Kitchen, Bar or both); on a tab it is not set up for, it says so
 * and shows nothing. The server, not this page, decides what the display may act on. A manager or the
 * owner previewing from a browser picks any station of the active location. Queries are the
 * truth (polled every 20 s, refetched on focus and reconnect); realtime hints only make them
 * refetch sooner. Every action is attributed to the PIN session and goes straight to the server.
 * Read from across the kitchen (Phase 7.5): every state has its word and its shape, and nothing
 * that changes a ticket can be pressed while the server cannot be reached (ADR-037).
 */
export function KdsPage({ route }: { route: KdsTab }) {
  const s = useSession();
  const me = s.me;
  const isDevice = me?.principal === 'device';
  const locationId = isDevice ? (me?.terminal?.location_id ?? '') : (me?.active_location_id ?? '');
  const timeZone =
    me?.locations.find((l) => l.id === locationId)?.timezone ??
    me?.business?.timezone ??
    'Asia/Manila';
  const employeeName = isDevice
    ? (me?.terminal_session?.employee_display_name ?? '')
    : (me?.employee?.display_name ?? '');
  const title = KDS_TAB_LABEL[route];

  const stations = useQuery({
    queryKey: kdsKeys.stations(locationId),
    enabled: !!locationId && !isDevice,
    staleTime: 60_000,
    queryFn: () => fetchStations(locationId),
  });
  const [choice, setChoice] = useState<string | null>(null);
  const list = stations.data ?? [];
  // a device: only a station assigned to it on this tab (never its home station on another tab)
  const device = isDevice ? resolveDeviceStation(me?.terminal, route, choice) : null;
  const stationId = device
    ? device.kind === 'station'
      ? device.stationId
      : null
    : choice && list.some((x) => x.id === choice)
      ? choice
      : ((list.find((x) => x.production_route?.code === route) ?? list[0])?.id ?? null);

  if (device?.kind === 'not_assigned') {
    return <NotAssigned tab={route} openTab={device.openTab} assigned={device.assigned} />;
  }

  if (!stationId) {
    if (!isDevice && stations.isLoading)
      return (
        <div className="d-page">
          <Loading label="Loading the stations…" />
        </div>
      );
    // a failed read is not "no station": say what happened and offer to try again
    if (!isDevice && stations.error)
      return (
        <div className="d-page">
          <ErrorState
            title="Could not load the stations"
            message={errorText(stations.error)}
            onRetry={() => void stations.refetch()}
          />
        </div>
      );
    return (
      <div className="d-page">
        <Surface>
          <div className="d-stack d-stack--tight">
            <h2>{title} display</h2>
            <p className="d-muted" data-testid="kds-unbound">
              {isDevice
                ? 'This terminal is not bound to a production station. The owner binds it under Devices (kind KDS, then the station).'
                : 'No production station at this location yet. Create one under Settings, Production stations.'}
            </p>
          </div>
        </Surface>
      </div>
    );
  }

  return (
    <StationBoard
      key={stationId}
      stationId={stationId}
      locationId={locationId}
      timeZone={timeZone}
      title={title}
      employeeName={employeeName}
      isDevice={isDevice}
      canOperate={s.can('kds.operate')}
      otherTabs={isDevice ? otherTabs(me?.terminal, route) : []}
      sameTab={device?.kind === 'station' ? device.choices.filter((c) => c.id !== stationId) : []}
      onPick={setChoice}
      picker={
        device?.kind === 'station' ? (
          device.choices.length > 1 ? (
            <Select
              aria-label="Station"
              value={stationId}
              onChange={(e) => setChoice(e.target.value)}
              data-testid="kds-station"
            >
              {device.choices.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name}
                </option>
              ))}
            </Select>
          ) : null
        ) : !isDevice && list.length > 1 ? (
          <Select
            aria-label="Station"
            value={stationId}
            onChange={(e) => setChoice(e.target.value)}
            data-testid="kds-station"
          >
            {list.map((st) => (
              <option key={st.id} value={st.id}>
                {st.name} · {st.production_route?.code ?? '—'}
              </option>
            ))}
          </Select>
        ) : null
      }
    />
  );
}

/** A device on a tab it is not set up for: say so, show no ticket, and offer the tab it opens on. */
function NotAssigned({
  tab,
  openTab,
  assigned,
}: {
  tab: KdsTab;
  openTab: KdsTab;
  assigned: KdsStation[];
}) {
  const navigate = useNavigate();
  const names = assigned.map((st) => st.name || KDS_TAB_LABEL[kdsTabOf(st.route_code)]).join(', ');
  return (
    <div className="d-page">
      <Surface>
        <div className="d-stack d-stack--tight">
          <h2>{KDS_TAB_LABEL[tab]} display</h2>
          <p className="d-muted" data-testid="kds-not-assigned">
            This display is not set up for the {KDS_TAB_LABEL[tab]}. It shows: {names}. The owner
            can add the {KDS_TAB_LABEL[tab]} under Devices.
          </p>
          <div>
            <Button onClick={() => navigate(KDS_TAB_PATH[openTab])} data-testid="kds-open-assigned">
              Open {KDS_TAB_LABEL[openTab]}
            </Button>
          </div>
        </div>
      </Surface>
    </div>
  );
}

/**
 * Another station of this display, with how many of its tickets wait ("Bar · 1"): the other tab's stations, or a
 * second station on this tab. It chimes when a ticket not announced before arrives there (the page-wide registry:
 * never on a first read, a refetch, a reconnect or a tab switch), so staff on the Kitchen tab hear the Bar. Only
 * ticket ids are read: the other station's tickets never enter this board.
 */
function WaitingBadge({
  locationId,
  label,
  stationIds,
  soundOn,
  onOpen,
  testId,
}: {
  locationId: string;
  label: string;
  stationIds: string[];
  soundOn: boolean;
  onOpen: () => void;
  testId: string;
}) {
  const key = kdsKeys.waiting(stationIds);
  const waiting = useQuery({
    queryKey: key,
    queryFn: () => fetchStationsWaiting(stationIds),
    refetchInterval: KDS_DEFAULTS.kdsPollSeconds * 1000,
    refetchOnMount: 'always',
  });
  useEffect(() => {
    if (!waiting.data) return;
    let fresh = 0;
    for (const id of stationIds)
      fresh += kdsHeard.fresh(
        `ticket:${id}`,
        waiting.data.filter((t) => t.production_station_id === id).map((t) => t.id),
      ).length;
    if (fresh > 0 && soundOn) playNewTicket();
  }, [waiting.data, stationIds, soundOn]);
  const count = waiting.data?.length ?? null;
  return (
    <>
      {stationIds.map((id) => (
        <WaitingHints key={id} topic={topicStation(locationId, id)} queryKey={key} />
      ))}
      <Button
        variant="ghost"
        onClick={onOpen}
        data-testid={testId}
        data-count={count ?? ''}
        aria-label={`${label}: ${count ?? 'unknown'} waiting`}
      >
        {label} · {count ?? '…'}
      </Button>
    </>
  );
}

function WaitingHints({ topic, queryKey }: { topic: string; queryKey: readonly string[] }) {
  useRealtimeInvalidate(topic, [queryKey]);
  return null;
}

type CardMode = 'queue' | 'cancelled' | 'recent';

interface CardActions {
  itemStart: (it: ProductionItem) => void;
  itemReady: (it: ProductionItem) => void;
  itemUndo: (it: ProductionItem) => void;
  itemRecall: (it: ProductionItem) => void;
  startAll: (t: Ticket) => void;
  readyAll: (t: Ticket) => void;
  dismiss: (t: Ticket) => void;
  undo: (t: Ticket) => void;
  recall: (t: Ticket) => void;
  retry: (target: string) => void;
  discard: (target: string) => void;
}

function StationBoard({
  stationId,
  locationId,
  timeZone,
  title,
  employeeName,
  isDevice,
  canOperate,
  otherTabs: others,
  sameTab,
  onPick,
  picker,
}: {
  stationId: string;
  locationId: string;
  timeZone: string;
  title: string;
  employeeName: string;
  isDevice: boolean;
  canOperate: boolean;
  otherTabs: Array<{ tab: KdsTab; stationIds: string[] }>;
  /** A device's other stations on this tab (a picker shows them; each gets its waiting count too). */
  sameTab: KdsStation[];
  onPick: (stationId: string) => void;
  picker: ReactNode;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const online = useOnline();
  const now = useClock(1000);
  const warnSetting = Number(useSetting('kds.warn_seconds'));
  const lateSetting = Number(useSetting('kds.late_seconds'));
  const undoWindow = Number(useSetting('kds.undo_window_seconds'));
  const chimeDefault = useSetting('kds.chime_new_ticket') === true;
  const [soundChoice, setSoundChoice] = useState<boolean | null>(() => readSoundChoice());
  const soundOn = soundChoice ?? chimeDefault;

  const poll = KDS_DEFAULTS.kdsPollSeconds * 1000;
  const station = useQuery({
    queryKey: kdsKeys.station(stationId),
    queryFn: () => fetchStation(stationId),
    staleTime: 60_000,
  });
  // a tab opened again reads its station at once, not at the next poll
  const queue = useQuery({
    queryKey: kdsKeys.queue(stationId),
    queryFn: () => fetchStationQueue(stationId),
    refetchInterval: poll,
    refetchOnMount: 'always',
  });
  const recent = useQuery({
    queryKey: kdsKeys.recent(stationId),
    queryFn: () => fetchStationRecent(stationId),
    refetchInterval: poll,
  });
  const cancelled = useQuery({
    queryKey: kdsKeys.cancelled(stationId),
    queryFn: () => fetchStationCancelled(stationId),
    refetchInterval: poll,
    refetchOnMount: 'always',
  });
  const tables = useTables(locationId);

  const topic = topicStation(locationId, stationId);
  useRealtimeInvalidate(topic, [
    kdsKeys.queue(stationId),
    kdsKeys.recent(stationId),
    kdsKeys.cancelled(stationId),
  ]);
  const channel = useChannelState(topic);
  const awake = useWakeLock(true);
  const actions = useKdsActions(isDevice ? null : locationId);
  const [reason, setReason] = useState<ReasonRequest | null>(null);
  const [showRecent, setShowRecent] = useState(false);

  const thresholds = effectiveThresholds(
    { warnSeconds: warnSetting, lateSeconds: lateSetting },
    station.data,
  );
  const queueTickets = sortTickets(queue.data ?? []);
  const cancelledTickets = cancelled.data ?? [];
  const recentTickets = recent.data ?? [];

  /* ── chimes: new ticket ids and newly cancelled items, each announced once (kdsHeard) ── */
  // The registry is page-wide and per station: a ticket the other tab's count already announced, a list shown again
  // from the cache, a failed read, a refetch or a reconnect never chime again; the first look at a station is quiet.
  useEffect(() => {
    if (!queue.data) return;
    const fresh = kdsHeard.fresh(
      `ticket:${stationId}`,
      queue.data.map((t) => t.id),
    );
    if (fresh.length > 0 && soundOn) playNewTicket();
  }, [queue.data, stationId, soundOn]);

  useEffect(() => {
    if (!queue.data || !cancelled.data) return;
    const ids = [...queue.data, ...cancelled.data].flatMap((t) =>
      t.production_item.filter((i) => i.status === 'cancelled').map((i) => i.id),
    );
    const fresh = kdsHeard.fresh(`cancelled:${stationId}`, ids);
    if (fresh.length > 0 && soundOn) playCancelled();
  }, [queue.data, cancelled.data, stationId, soundOn]);

  const toggleSound = () => {
    const next = !soundOn;
    primeAudio();
    writeSoundChoice(next);
    setSoundChoice(next);
    if (next) playNewTicket();
  };

  /* ── actions ── */
  const label = (t: Ticket) => t.orders?.order_number ?? 'ticket';
  const recall = (t: Ticket) =>
    setReason({
      title: `Recall ${label(t)}`,
      label: 'Why is it coming back?',
      required: true,
      confirmLabel: 'Recall ticket',
      danger: true,
      onConfirm: async (why) => {
        const r = await actions.run(
          'ticket.recall',
          { ticket_id: t.id, reason: why },
          t.id,
          'Recall',
        );
        if (r.ok) {
          setShowRecent(false);
          toast(`${label(t)} is back in the queue`);
        }
      },
    });
  const itemLabel = (it: ProductionItem) => it.order_item?.name_snapshot ?? 'item';
  const recallItem = (it: ProductionItem) =>
    setReason({
      title: `Recall ${itemLabel(it)}`,
      label: 'Why is it coming back?',
      required: true,
      confirmLabel: 'Recall item',
      danger: true,
      onConfirm: async (why) => {
        const r = await actions.run(
          'item.recall',
          { production_item_id: it.id, reason: why },
          it.id,
          'Recall',
        );
        if (r.ok) toast(`${itemLabel(it)} is back in preparation`);
      },
    });
  const afterUndo = (t: Ticket, r: ActionOutcome) => {
    if (r.ok) {
      setShowRecent(false);
      return;
    }
    if (r.unconfirmed) return;
    if (/undo window/i.test(r.error.message)) recall(t);
    else toast(r.error.message, 'danger');
  };
  const cardActions: CardActions = {
    itemStart: (it) =>
      void actions.run('item.start', { production_item_id: it.id }, it.id, 'Start'),
    itemReady: (it) =>
      void actions.run('item.ready', { production_item_id: it.id }, it.id, 'Ready'),
    itemUndo: (it) =>
      void actions
        .run('item.undo', { production_item_id: it.id }, it.id, 'Undo', { quiet: true })
        .then((r) => {
          if (r.ok || r.unconfirmed) return;
          // the window passed while the button was on screen: the same tap continues as a recall
          if (/undo window/i.test(r.error.message)) recallItem(it);
          else toast(r.error.message, 'danger');
        }),
    itemRecall: recallItem,
    startAll: (t) => void actions.run('ticket.start_all', { ticket_id: t.id }, t.id, 'Start all'),
    readyAll: (t) => void actions.run('ticket.ready_all', { ticket_id: t.id }, t.id, 'Ready all'),
    dismiss: (t) => void actions.run('ticket.dismiss', { ticket_id: t.id }, t.id, 'Dismiss'),
    undo: (t) =>
      void actions
        .run('ticket.undo', { ticket_id: t.id }, t.id, 'Undo', { quiet: true })
        .then((r) => afterUndo(t, r)),
    recall,
    retry: actions.retry,
    discard: actions.discard,
  };

  const tableName = (id: string | null) =>
    id ? ((tables.data ?? []).find((x) => x.id === id)?.name ?? 'table') : null;
  const refreshed = queue.dataUpdatedAt
    ? clockTime(new Date(queue.dataUpdatedAt).toISOString(), timeZone, true)
    : '—';
  // only a read that came back can say there is nothing to cook
  const empty = !!queue.data && queueTickets.length === 0 && cancelledTickets.length === 0;
  const cardProps = {
    now,
    thresholds,
    undoWindow,
    timeZone,
    tableName,
    pending: actions.pending,
    canOperate,
    online,
    on: cardActions,
  };

  return (
    <div className="d-page d-page--wide live-board kds" data-testid="kds" data-station={stationId}>
      <PageHeader
        eyebrow={station.data?.code ?? undefined}
        title={station.data?.name ?? title}
        actions={
          <>
            {others.map((o) => (
              <WaitingBadge
                key={o.tab}
                locationId={locationId}
                label={KDS_TAB_LABEL[o.tab]}
                stationIds={o.stationIds}
                soundOn={soundOn}
                onOpen={() => navigate(KDS_TAB_PATH[o.tab])}
                testId={`kds-other-tab-${o.tab}`}
              />
            ))}
            {sameTab.map((st) => (
              <WaitingBadge
                key={st.id}
                locationId={locationId}
                label={st.name}
                stationIds={[st.id]}
                soundOn={soundOn}
                onOpen={() => onPick(st.id)}
                testId={`kds-same-tab-${st.code}`}
              />
            ))}
            {picker}
            <span className="live-meta">
              <LivePill status={channel} online={online} testId="kds-connection" />
              <span className="d-num" data-testid="kds-refreshed">
                refreshed {refreshed}
              </span>
              {awake && (
                <span title="The screen stays awake while this display is open">awake</span>
              )}
            </span>
            <Button
              variant="ghost"
              aria-pressed={soundOn}
              onClick={toggleSound}
              data-testid="kds-sound"
            >
              Sound {soundOn ? 'on' : 'off'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowRecent(true)}
              data-testid="kds-recent-open"
            >
              Recent ({recentTickets.length})
            </Button>
            {employeeName && <span className="d-faint d-break">{employeeName}</span>}
          </>
        }
      />

      {!canOperate && (
        <Notice tone="warning" role="status">
          View only: your role has no kds.operate permission.
        </Notice>
      )}
      {queue.error && (
        <ErrorState
          title={queue.data ? 'Could not refresh the tickets' : 'Could not load the tickets'}
          message={errorText(queue.error)}
          onRetry={() => void queue.refetch()}
        />
      )}
      {queue.isLoading && <Loading label="Loading the tickets…" />}

      <div className="live-grid" data-testid="kds-grid">
        {cancelledTickets.map((t) => (
          <TicketCard key={t.id} t={t} mode="cancelled" {...cardProps} />
        ))}
        {queueTickets.map((t) => (
          <TicketCard key={t.id} t={t} mode="queue" {...cardProps} />
        ))}
      </div>
      {empty && (
        <EmptyState title="No tickets" testId="kds-empty">
          New orders appear here the moment they are sent.
        </EmptyState>
      )}

      <Sheet open={showRecent} onClose={() => setShowRecent(false)} title="Recent tickets">
        <div className="d-stack" data-testid="kds-recent">
          {recentTickets.length === 0 && (
            <EmptyState
              title={`Nothing bumped in the last ${KDS_DEFAULTS.recallWindowMinutes / 60} hours.`}
            />
          )}
          {recentTickets.map((t) => (
            <TicketCard key={t.id} t={t} mode="recent" {...cardProps} />
          ))}
        </div>
      </Sheet>
      <ReasonSheet request={reason} onClose={() => setReason(null)} />
    </div>
  );
}

function TicketCard({
  t,
  mode,
  now,
  thresholds,
  undoWindow,
  timeZone,
  tableName,
  pending,
  canOperate,
  online,
  on,
}: {
  t: Ticket;
  mode: CardMode;
  now: number;
  thresholds: AgeThresholds;
  undoWindow: number;
  timeZone: string;
  tableName: (id: string | null) => string | null;
  pending: Record<string, PendingAction>;
  canOperate: boolean;
  online: boolean;
  on: CardActions;
}) {
  const o = t.orders;
  const items = ticketItems(t);
  const live = items.filter((i) => i.status !== 'cancelled');
  const bucket = mode === 'queue' ? ageBucket(t.sent_at, now, thresholds) : null;
  const cls = [
    'live-card',
    'kds-card',
    mode === 'cancelled'
      ? 'live-card--cancelled'
      : mode === 'recent'
        ? 'live-card--ready'
        : t.status === 'queued'
          ? 'live-card--waiting'
          : 'live-card--progress',
    bucket === 'warn' && 'live-card--warn',
    bucket === 'late' && 'live-card--late',
  ]
    .filter(Boolean)
    .join(' ');
  const tp = pending[t.id];
  const busy = !!tp && tp.state === 'sending';
  const note =
    mode === 'recent'
      ? `ready ${formatAge(ageSeconds(t.ready_at ?? t.sent_at, now))} ago`
      : t.cancelled_at
        ? `cancelled ${clockTime(t.cancelled_at, timeZone)}`
        : 'cancelled';
  const scheduled =
    o?.type === 'advance' && o.scheduled_for ? clockTime(o.scheduled_for, timeZone) : null;
  const table = o?.dining_table_id ? tableName(o.dining_table_id) : null;
  const startAll = canOperate && mode === 'queue' && live.some((i) => i.status === 'queued');
  const readyAll = canOperate && mode === 'queue' && live.some((i) => i.status !== 'ready');

  return (
    <article
      className={cls}
      data-testid="kds-ticket"
      data-ticket={t.id}
      data-order={o?.order_number ?? ''}
      data-age={bucket ?? mode}
      aria-label={`Ticket ${o?.order_number ?? ''}`}
    >
      {mode === 'cancelled' && (
        <div className="kds-banner">
          <span className="live-mark live-mark--cross" aria-hidden />
          Cancelled
        </div>
      )}
      <div className="live-card__head">
        <div className="live-card__id">
          <OrderNo value={o?.order_number} />
          {o && (
            <div className="live-card__where">
              <span className="live-card__type">{ORDER_TYPE_LABEL[o.type]}</span>
              {table ? ` · ${table}` : ''}
            </div>
          )}
        </div>
        {bucket ? (
          <AgeClock seconds={ageSeconds(t.sent_at, now)} bucket={bucket} testId="kds-age" />
        ) : (
          <div className="live-age__note" data-testid="kds-age">
            {note}
          </div>
        )}
      </div>
      <div className="live-card__tags">
        {mode !== 'cancelled' && <TicketState status={t.status} />}
        {t.is_addition && (
          <span className="kds-addition">
            <span aria-hidden>+</span>ADDITION
          </span>
        )}
        {o?.customer_name && <Badge>{o.customer_name}</Badge>}
        {scheduled && <Badge tone="info">for {scheduled}</Badge>}
        <span className="d-faint d-mono">#{t.ticket_seq}</span>
      </div>
      {o?.notes && <div className="live-card__note">“{o.notes}”</div>}

      <div className="kds-items">
        {items.map((it) => (
          <ItemRow
            key={it.id}
            it={it}
            mode={mode}
            now={now}
            undoWindow={undoWindow}
            pending={pending[it.id]}
            disabled={!canOperate || busy || !online}
            on={on}
          />
        ))}
      </div>

      {tp?.state === 'unconfirmed' && (
        <Unconfirmed
          label={tp.label}
          prefix="kds"
          onRetry={() => on.retry(t.id)}
          onDiscard={() => on.discard(t.id)}
        />
      )}
      {busy && (
        <div className="live-sending" role="status">
          <span className="d-spinner" aria-hidden />
          Sending {tp.label.toLowerCase()}…
        </div>
      )}

      {(startAll || readyAll) && (
        <div className="live-card__foot">
          {startAll && (
            <Button
              size="lg"
              variant="ghost"
              busy={busy}
              disabled={!online}
              onClick={() => on.startAll(t)}
              data-testid="kds-start-all"
            >
              Start all
            </Button>
          )}
          {readyAll && (
            <Button
              size="lg"
              variant="primary"
              busy={busy}
              disabled={!online}
              onClick={() => on.readyAll(t)}
              data-testid="kds-ready-all"
            >
              Ready all
            </Button>
          )}
        </div>
      )}
      {canOperate && mode === 'cancelled' && (
        <div className="live-card__foot">
          <Button
            size="lg"
            variant="danger"
            busy={busy}
            disabled={!online}
            onClick={() => on.dismiss(t)}
            data-testid="kds-dismiss"
          >
            Dismiss
          </Button>
        </div>
      )}
      {canOperate && mode === 'recent' && (
        <div className="live-card__foot">
          {canUndo(t.ready_at, now, undoWindow) ? (
            <Button
              size="lg"
              variant="ghost"
              busy={busy}
              disabled={!online}
              onClick={() => on.undo(t)}
              data-testid="kds-undo"
            >
              Undo
            </Button>
          ) : (
            <Button
              size="lg"
              variant="ghost"
              busy={busy}
              disabled={!online}
              onClick={() => on.recall(t)}
              data-testid="kds-recall"
            >
              Recall…
            </Button>
          )}
        </div>
      )}
    </article>
  );
}

function ItemRow({
  it,
  mode,
  now,
  undoWindow,
  pending,
  disabled,
  on,
}: {
  it: ProductionItem;
  mode: CardMode;
  now: number;
  undoWindow: number;
  pending: PendingAction | undefined;
  disabled: boolean;
  on: CardActions;
}) {
  const line = it.order_item;
  const name = line?.name_snapshot ?? 'item';
  const meta = lineMeta(line);
  const sending = pending?.state === 'sending';
  const off = disabled || sending;
  const queue = mode === 'queue';
  return (
    <div
      className={`kds-item kds-item--${it.status}`}
      data-testid="kds-item"
      data-status={it.status}
    >
      <div className="kds-item__line">
        <span className="kds-item__qty">{it.qty}×</span>
        <div className="kds-item__what">
          <div className="kds-item__name">{line?.name_snapshot ?? 'Item'}</div>
          {meta && <div className="kds-item__meta">{meta}</div>}
        </div>
      </div>
      <div className="kds-item__foot">
        <ItemState status={it.status} />
        {queue && it.status !== 'cancelled' && (
          <div className="kds-item__actions">
            {it.status === 'queued' && (
              <Button
                size="lg"
                variant="ghost"
                disabled={off}
                onClick={() => on.itemStart(it)}
                data-testid="kds-item-start"
                aria-label={`start ${name}`}
              >
                Start
              </Button>
            )}
            {(it.status === 'queued' || it.status === 'preparing') && (
              <Button
                size="lg"
                variant="primary"
                disabled={off}
                onClick={() => on.itemReady(it)}
                data-testid="kds-item-ready"
                aria-label={`ready ${name}`}
              >
                Ready
              </Button>
            )}
            {it.status === 'ready' &&
              (canUndo(it.ready_at, now, undoWindow) ? (
                <Button
                  size="lg"
                  variant="ghost"
                  disabled={off}
                  onClick={() => on.itemUndo(it)}
                  data-testid="kds-item-undo"
                  aria-label={`undo ${name}`}
                >
                  Undo
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="ghost"
                  disabled={off}
                  onClick={() => on.itemRecall(it)}
                  data-testid="kds-item-recall"
                  aria-label={`recall ${name}`}
                >
                  Recall…
                </Button>
              ))}
          </div>
        )}
      </div>
      {pending?.state === 'unconfirmed' && (
        <Unconfirmed
          label={pending.label}
          prefix="kds"
          onRetry={() => on.retry(it.id)}
          onDiscard={() => on.discard(it.id)}
        />
      )}
    </div>
  );
}
